import { IonBadge, IonButton, IonGrid, IonRow, IonText, useIonToast } from '@ionic/react';
import styles from './Report.module.css';
import { useWindowDimensions } from '@/shared/hooks/useWindowDimension';
import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCustodyReportsDownloadMutation } from '@/features/custodyReports/custodyReportsApi';
import { getShortDateTimeString } from '@/shared/helpers/formatters';
import { Capacitor } from '@capacitor/core';

type Props = {
  report: any;
  highlightReportId?: number | null;
};

const Report: React.FC<Props> = ({ report, highlightReportId }) => {
  const { t } = useTranslation();
  const { isMobileView } = useWindowDimensions();
  const { endsAt, startsAt, quarterInfo, id } = report;
  const [presentToast] = useIonToast();
  const isApp = Capacitor.isNativePlatform();

  const [downloadCustodyReport] = useCustodyReportsDownloadMutation();

  const shortDateTime = useMemo(() => {
    if (!endsAt) return '';

    return getShortDateTimeString(new Date(endsAt), true);
  }, [endsAt]);

  const formattedEndsAt = useMemo(() => {
    if (!endsAt) return '';

    return getShortDateTimeString(new Date(endsAt), false);
  }, [endsAt]);

  const formattedStartsAt = useMemo(() => {
    if (!startsAt) return '';

    return getShortDateTimeString(new Date(startsAt), false);
  }, [startsAt]);

  const handleDownloadReports = useCallback(async () => {
    try {
      downloadCustodyReport({
        id,
        startsAt: formattedStartsAt || '',
        endsAt: formattedEndsAt || ''
      }).unwrap();

      if (isApp) {
        presentToast({
          message: t('settings.custody_reports.download.success'),
          duration: 3000,
          position: 'top',
          color: 'success'
        });
      }
    } catch (err) {
      console.error('Failed to download custody reports', err);
      presentToast({
        message: t('settings.custody_reports.download.failed'),
        duration: 2000,
        position: 'top',
        color: 'danger'
      });
    }
  }, [downloadCustodyReport, formattedEndsAt, formattedStartsAt, id, isApp, presentToast, t]);

  return (
    <IonGrid className={styles.wrapper} key={report.id}>
      <IonRow className={isMobileView ? styles.rowMobile : styles.row}>
        <div className={styles.descriptionContainer}>
          <IonText>
            <Trans
              i18nKey="settings.custody_reports.report.title"
              values={{ year: quarterInfo.year, fiscalPeriod: quarterInfo.quarter }}
            />
            {highlightReportId === report.id && (
              <IonBadge color="success" className={styles.newBadge}>
                <IonText className={styles.newBadgeText}>{t('settings.custody_reports.report.new')}</IonText>
              </IonBadge>
            )}
          </IonText>
          <IonText color={'medium'} className={styles.dateTime}>
            <Trans
              i18nKey="settings.custody_reports.report.period"
              values={{ from: formattedStartsAt, until: formattedEndsAt }}
            />
          </IonText>
          <IonText color={'medium'} className={styles.dateTime}>
            <Trans i18nKey="settings.custody_reports.report.generated_on" values={{ dateTime: shortDateTime }} />
          </IonText>
        </div>

        <div className={styles.buttonContainer}>
          <IonButton
            fill="outline"
            expand={isMobileView ? 'block' : undefined}
            onClick={() => handleDownloadReports()}
            disabled={!report}
            size="small"
            className={styles.button}
          >
            {t('settings.custody_reports.download')}
          </IonButton>
        </div>
      </IonRow>
    </IonGrid>
  );
};

export default Report;
